/**
 * Digital acknowledgement + score history for the staff-facing appraisal.
 * Replaces chasing a wet signature: the employee ticks that the appraisal was
 * discussed and understood, and that timestamp lands on the PDF.
 */
import { useLang } from '../../../i18n/LangContext';
import React, { useState } from 'react';
import { CheckCircle2, Loader2, PenLine, TrendingUp } from 'lucide-react';
import { BAND_STYLE } from './shared';

export const ScoreHistory = ({ reviews, currentId }) => {
  const { t } = useLang();
  const rows = (reviews || [])
    .filter((r) => r.status === 'published' && r.final_score !== null && r.final_score !== undefined)
    .slice(0, 3)
    .reverse();
  if (rows.length < 2) return null;
  const move = Math.round((rows[rows.length - 1].final_score - rows[0].final_score) * 10) / 10;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="score-history">
      <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
        <TrendingUp size={15} className="text-teal-600" /> {t('My score history')}
      </h2>
      <div className="grid gap-2 sm:grid-cols-3">
        {rows.map((r) => (
          <div
            key={r.id}
            className={`rounded-lg border p-3 ${r.id === currentId
              ? 'border-teal-400 bg-teal-50/60' : 'border-slate-200'}`}
            data-testid={`score-history-${r.id}`}
          >
            <div className="text-[11px] uppercase tracking-wider text-slate-500 truncate">{r.cycle_name}</div>
            <div className="text-2xl font-semibold text-slate-900">{r.final_score}<span className="text-sm text-slate-400">/100</span></div>
            <span className={`inline-block mt-1 rounded-full border px-2 py-0.5 text-[11px] ${BAND_STYLE[r.band]}`}>
              {r.band} · {r.band_label}
            </span>
          </div>
        ))}
      </div>
      <p className={`mt-2 text-xs ${move > 0 ? 'text-emerald-700' : move < 0 ? 'text-red-700' : 'text-slate-500'}`}
         data-testid="score-history-trend">
        {move > 0 ? `Up ${move} points since ${rows[0].cycle_name}.`
          : move < 0 ? `Down ${Math.abs(move)} points since ${rows[0].cycle_name}.`
            : `Unchanged since ${rows[0].cycle_name}.`}
      </p>
    </div>
  );
};

export const AcknowledgeCard = ({ review, onAcknowledge, busy }) => {
  const { t } = useLang();
  const [ticked, setTicked] = useState(false);
  const [comment, setComment] = useState('');
  if (!review || review.status !== 'published') return null;

  if (review.acknowledged_at) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5" data-testid="ack-done">
        <div className="flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            {t('You acknowledged this appraisal on')}{' '}
            <b>{String(review.acknowledged_at).replace('T', ' ').slice(0, 16)}</b>.
            {review.acknowledgement_comment && (
              <p className="mt-1 text-xs text-emerald-800">
                <span className="font-medium">{t('Your note')}: </span>{review.acknowledgement_comment}
              </p>
            )}
            <p className="mt-1 text-xs text-emerald-700">
              {t('It now shows on your appraisal letter — no signature needed.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-3.5" data-testid="ack-card">
      <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
        <PenLine size={15} className="text-teal-600" /> Acknowledge this appraisal
      </h3>
      <label className="mt-2 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
        <input
          type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)}
          className="mt-0.5" data-testid="ack-checkbox"
        />
        <span>
          I confirm this appraisal has been <b>discussed with my manager and I have understood it</b>.
          Acknowledging is not the same as agreeing — add a note below if you disagree with anything.
        </span>
      </label>
      <textarea
        value={comment} onChange={(e) => setComment(e.target.value)}
        rows={2} maxLength={1000}
        placeholder="Optional note for the record (e.g. what you'd like support on)"
        className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none"
        data-testid="ack-comment"
      />
      <button
        onClick={() => onAcknowledge(comment)}
        disabled={!ticked || busy === 'ack'}
        className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:bg-slate-300"
        data-testid="ack-submit"
      >
        {busy === 'ack' ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
        Confirm and record
      </button>
    </div>
  );
};
